import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { communityRouteName } from '@/app/community/routes';
import { SOCIAL_LISTENING_ROUTE_NAMES } from '@/app/socialListening/constants';
import { INTENT_TYPES, INTENT_TYPES_BY_ROUTE_NAME } from '@/components/VisionAi/constants';
import { BRAND, ORGANIZATION, USER } from '@/models/auth/permissions.enum';
import { useIdentityStore } from '@/stores/identity';

export function useVisionAiPermissions() {
  const identityStore = useIdentityStore();
  const route = useRoute();

  const userCanAccessVisionAiForSomeBrand = computed(() =>
    identityStore.allUserBrands.some((brand) => {
      const brandAccess = identityStore.guardSome(
        [BRAND.VISION.CAN_ACCESS_VISION_AI_COMMUNITY, BRAND.VISION.CAN_ACCESS_VISION_AI_DASHBOARDS],
        brand,
      );
      const userAccess = identityStore.guard(USER.VISION.CAN_ACCESS_VISION_AI, brand);
      return brandAccess && userAccess;
    }),
  );

  const userCanAccessVisionAiForSomeOrg = computed(() =>
    identityStore.allUserBrands.some((brand) => {
      const orgAccess = identityStore.guard(
        ORGANIZATION.LISTENING.CAN_ACCESS_VISION_AI_SOCIAL_LISTENING,
        brand,
      );
      const userAccess = identityStore.guard(USER.VISION.CAN_ACCESS_VISION_AI, brand);
      return orgAccess && userAccess;
    }),
  );

  const userCanAccessVisionAiForSomeBrandOrOrg = computed(
    () => userCanAccessVisionAiForSomeBrand.value || userCanAccessVisionAiForSomeOrg.value,
  );

  const someBrandCanAccessDashboardsAndVisionAi = computed(() =>
    identityStore.allUserBrands.some((brand) => {
      const brandCanAccessDashboards = identityStore.guard(
        BRAND.VISION.CAN_ACCESS_VISION_AI_DASHBOARDS,
        brand,
      );
      const userCanAccessVisionAi = identityStore.guard(USER.VISION.CAN_ACCESS_VISION_AI, brand);
      return brandCanAccessDashboards && userCanAccessVisionAi;
    }),
  );
  const someUserBrandCanAccessVisionAiConversational = computed(() =>
    identityStore.allUserBrands.some((brand) => {
      const userBrandCanAccessConversational = identityStore.guard(
        USER.VISION.CAN_ACCESS_VISION_AI_CONVERSATIONAL,
        brand,
      );
      return userBrandCanAccessConversational;
    }),
  );
  const userCanAccessCommunityVisionAiForCurrentBrand = computed(
    () =>
      identityStore.guard(BRAND.VISION.CAN_ACCESS_VISION_AI_COMMUNITY) &&
      identityStore.guard(USER.VISION.CAN_ACCESS_VISION_AI),
  );

  const userCanAccessSocialListeningVisionAiForCurrentBrand = computed(
    () =>
      identityStore.guard(ORGANIZATION.LISTENING.CAN_ACCESS_VISION_AI_SOCIAL_LISTENING) &&
      identityStore.guard(USER.VISION.CAN_ACCESS_VISION_AI),
  );

  const userHasVisionAiPermissionForCurrentRoute = computed(() => {
    if (route && route.name?.startsWith(communityRouteName)) {
      return userCanAccessCommunityVisionAiForCurrentBrand.value;
    }

    if (route && route.name === SOCIAL_LISTENING_ROUTE_NAMES.OVERVIEW) {
      return userCanAccessSocialListeningVisionAiForCurrentBrand.value;
    }

    const intent = INTENT_TYPES_BY_ROUTE_NAME?.[route?.name];
    switch (intent) {
      case INTENT_TYPES.TRENDS:
      case INTENT_TYPES.TOPICS: {
        return userCanAccessSocialListeningVisionAiForCurrentBrand.value;
      }
      case INTENT_TYPES.DASHBOARD_REPORTS: {
        return someBrandCanAccessDashboardsAndVisionAi.value;
      }
      case INTENT_TYPES.UNIFIED: {
        return userCanAccessVisionAiForSomeBrandOrOrg.value;
      }
      default:
        return false;
    }
  });

  const userHasVisionAiButNotConversational = computed(
    () =>
      userCanAccessVisionAiForSomeBrandOrOrg.value &&
      (!someBrandCanAccessDashboardsAndVisionAi.value ||
        !someUserBrandCanAccessVisionAiConversational.value),
  );

  return {
    userCanAccessVisionAiForSomeBrandOrOrg,
    userHasVisionAiPermissionForCurrentRoute,
    someBrandCanAccessDashboardsAndVisionAi,
    someUserBrandCanAccessVisionAiConversational,
    userHasVisionAiButNotConversational,
  };
}
